import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useAuth } from "../../../context/AuthContext";
import { useNavigate, useParams } from "react-router-dom";
import { IoIosArrowRoundBack } from "react-icons/io";
import QuestionSection from "../../ui/QuestionSection/QuestionSection";
import CommonFooter from "../../CommonFooter/CommonFooter";
import classes from "./GeneralRequirements.module.css";
import { env } from "../../../constants/env";
import axios from "axios";
import CommonButton from "../../ui/CommonButton/CommonButton";

const GeneralRequirements = () => {
  const { labId } = useParams();
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [currentSet, setCurrentSet] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isfetching, setIsFetching] = useState(false);

  const userRole = user?.role;

  const [editable, setEditable] = useState(userRole === "ASSESSOR");

  const handleAnswerChange = (questionId, score, comment, points) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: { score, comment, points },
    }));
  };

  useEffect(() => {
    if (!labId) return;
    fetchAccreditation();
  }, [labId, token, user._id]);

  const fetchAccreditation = async () => {
    setIsFetching(true)
    axios
      .get(`${env.kataApiUrl}/api/accreditation`, {
        params: {
          labId,
          section: "General requirements",
          name: "SADCAS F134-A",
        },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (res.data.success && res.data.accreditation) {
          const accreditation = res.data.accreditation;

          // Check if current user is the assigned auditor
          if (accreditation.auditorId !== user._id || userRole !== "ASSESSOR") {
            setEditable(false);
          }

          if (accreditation.answers) {
            // Prefill answers
            const savedAnswers = accreditation.answers || [];
            const mapped = savedAnswers.reduce((acc, a) => {
              acc[a.questionId] = {
                score: a.score || "",
                comment: a.comment || "",
                points: a.points || 0,
              };
              return acc;
            }, {});
            setAnswers(mapped);
            setEditable(false); // ✅ answers exist → read-only by default
          } else {
            setEditable(true); // ✅ no answers → open in edit mode
          }
        }
      })
      .catch((err) => {
        console.error("Failed to fetch accreditation:", err);
      }).finally(() => {
        setIsFetching(false);
      });
  };

  const questionSets = [
    {
      questions: [
        {
          questionId: "Q1",
          content:
            "Are laboratory activities undertaken impartially? How is the laboratory structured and managed to safeguard impartiality?",
          number: "a",
          multiline: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q2",
          content:
            "What evidence is available to show management’s commitment to impartiality?",
          number: "b",
          multiline: true,
          placeholder: "Add comment",
        },
        {
          questionId: "Q3",
          content:
            "What measures are in place to ensure that the laboratory takes responsibility for impartiality and avoids pressures?",
          number: "c",
          multiline: true,
          placeholder: "Add comment",
        },
      ],
      render: (onAnswerChange, answers) => (
        <QuestionSection
          number="4.1"
          title="Impartiality"
          accreditation
          showPointsField={false}
          questions={questionSets[0].questions} // refer the array here
          onAnswerChange={editable ? onAnswerChange : () => {}}
          answers={answers}
          disabled={!editable}
        />
      ),
    },
  ];

  const handleNext = () => {
    if (currentSet < questionSets.length - 1) setCurrentSet((prev) => prev + 1);
  };

  const handlePrevious = () => {
    if (currentSet > 0) setCurrentSet((prev) => prev - 1);
  };

  const handleDone = async () => {
    setIsSubmitting(true);
    try {
      const answersArray = Object.entries(answers).map(([questionId, data]) => {
        // Find the question object to get content
        let questionContent;
        questionSets.forEach((set) => {
          set.questions?.forEach((q) => {
            if (q.questionId === questionId) questionContent = q.content;
          });
        });

        console.log("Answers array:", questionContent);

        return {
          questionId,
          content: questionContent, // <-- include content here
          score: data.score || undefined,
          comment: data.comment,
          points: data.points,
        };
      });

      const response = await fetch(`${env.kataApiUrl}/api/accreditation`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: "SADCAS F134-A",
          labId: labId,
          section: "General requirements",
          auditorId: user._id,
          answers: answersArray,
        }),
      });

      if (!response.ok) throw new Error("Submission failed");

      fetchAccreditation();

      toast.success("Updated successfully!");
    } catch (error) {
      toast.error(error.message || "Failed to submit assessment");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isfetching) {
    return <div className={classes.loading}>Loading...</div>;
  }

  return (
    <div
      className={`${classes.wrapper} ${!editable ? classes.fromParams : ""}`}
    >
      <div className="d-flex justify-content-between align-items-center">
        <div
          className="d-flex align-items-center gap-2 mb-4"
          onClick={() => navigate(-1)}
          role="button"
        >
          <IoIosArrowRoundBack size={35} />

          <h2 className="m-0">Clause 4: General Requirements</h2>
        </div>
        {userRole === "ASSESSOR" && !editable && (
          <CommonButton
            className={classes.editButton}
            onClick={() => setEditable(true)}
            title="Edit"
          />
        )}
      </div>

      {!editable ? (
        <div className={classes.infoText}>
          ✅ Your SADCAS A - Clause 4: General Requirements has been submitted.
        </div>
      ) : (
        questionSets[currentSet].render(handleAnswerChange, answers)
      )}

      {editable && (
        <CommonFooter
          handlePrevious={handlePrevious}
          handleNext={handleNext}
          handleDone={handleDone}
          cancelText="Cancel"
          handleCancel={() => {
            setEditable(false);
          }}
          currentSet={currentSet}
          questionSets={questionSets}
          doneText={isSubmitting ? "Submitting..." : "Update"}
          disabled={isSubmitting}
        />
      )}
    </div>
  );
};

export default GeneralRequirements;
